import * as React from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { Loader } from '@/components/custom/loader';
import { FormCheck, FormField } from '@/components/custom/form';
import { Button } from '@/components/ui/button';
import { ModalTaxonomy } from '@/components/custom/modal/modal-taxonomy';
import { FiPlus, FiX } from 'react-icons/fi';
import { Badge } from '@/components/ui/badge';

const MultiSelect = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('multi-select', 'flex min-h-[40px] items-center rounded-md border border-input bg-bright px-2 py-1 text-sm', className)}
    {...props}
  />
));
MultiSelect.displayName = 'MultiSelect';

const MultiSelectValueContainer = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('multi-select-value-container', 'flex grow basis-0 flex-wrap items-center gap-[2px]', className)}
    {...props}
  />
));
MultiSelectValueContainer.displayName = 'MultiSelectValueContainer';

const MultiSelectIndicator = React.forwardRef(({ className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn('multi-select-indicator', 'flex items-center justify-center bg-transparent p-2 text-primary', className)}
    {...props}
  />
));
MultiSelectIndicator.displayName = 'MultiSelectIndicator';

const MultiSelectPlaceholder = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('multi-select-placeholder', 'select-none text-muted-foreground', className)}
    {...props}
  />
));
MultiSelectPlaceholder.displayName = 'MultiSelectPlaceholder';

/**
 * Componente que gerencia a exibição e a seleção (via modal) de taxonomias (matérias).
 *
 * @param {object} props
 * @param {boolean} props.isLoading - Indica se a lista completa de taxonomias está sendo carregada.
 * @param {Array} props.taxonomies - A lista completa de taxonomias (para o Modal).
 * @param {Array} props.selectedTaxonomies - As taxonomias atualmente selecionadas (form.values.selectedTaxonomies).
 * @param {function} props.setFieldValue - Função do Formik para atualizar o campo 'selectedTaxonomies'.
 * @param {object} props.errors - Objeto de erros do Formik.
 * @param {object} props.touched - Objeto de touched do Formik.
 * @param {boolean} props.isOpenModalTaxonomies - Estado do modal.
 * @param {function} props.setIsOpenModalTaxonomies - Setter para o estado do modal.
 */
export function FormTaxonomy({
  className,
  isLoading,
  taxonomies,
  selectedTaxonomies,
  setFieldValue,
  errors,
  touched,
  isOpenModalTaxonomies,
  setIsOpenModalTaxonomies,
  buttonVariant = 'outline',
  variant = 'multi-select',
  placeholder = 'Adicione...',
  label = 'Matérias',
  ...props
}) {
  const { t } = useTranslation();

  // Lógica para remover uma taxonomia selecionada ao desmarcar o FormCheck
  const handleRemoveTaxonomy = taxonomyToRemove => {
    const newSelectedTaxonomies = selectedTaxonomies.filter(t2 => taxonomyToRemove.id !== t2.id);
    setFieldValue('selectedTaxonomies', newSelectedTaxonomies);
  };

  return (
    <>
      <FormField
        label={label}
        hasError={errors.selectedTaxonomies && touched.selectedTaxonomies}
        errorText={errors.selectedTaxonomies}
        className={cn('c-filter-check', className)}
        {...props}
      >
        {isLoading ? (
          <Loader />
        ) : variant === 'multi-select' ? (
          <MultiSelect>
            <MultiSelectValueContainer>
              {(selectedTaxonomies?.length === 0 || selectedTaxonomies === undefined) && <MultiSelectPlaceholder>{placeholder}</MultiSelectPlaceholder>}

              {/* 1. Exibição das Matérias Selecionadas */}
              {selectedTaxonomies?.length > 0 &&
                selectedTaxonomies?.map(t => (
                  <Badge
                    variant="primary-light"
                    size="sm"
                  >
                    {t.name}
                    <button
                      className="ml-1"
                      onClick={() => {
                        const newSelectedTaxonomies = selectedTaxonomies?.filter(t2 => t.id !== t2.id);

                        setFieldValue('selectedTaxonomies', newSelectedTaxonomies);
                      }}
                      type="button"
                    >
                      <FiX size={12} />
                    </button>
                  </Badge>
                ))}
            </MultiSelectValueContainer>

            {/* 2. Botão para Abrir o Modal de Seleção */}
            <MultiSelectIndicator
              onClick={() => setIsOpenModalTaxonomies(true)}
              title={selectedTaxonomies?.length > 0 ? 'Editar matérias' : 'Adicionar matérias'}
              aria-label={selectedTaxonomies?.length > 0 ? 'Editar matérias' : 'Adicionar matérias'}
            >
              <FiPlus size={16} />
            </MultiSelectIndicator>
          </MultiSelect>
        ) : (
          <>
            {/* 1. Exibição das Matérias Selecionadas */}
            {selectedTaxonomies?.length > 0 && (
              <div className="!mb-3 flex flex-wrap gap-x-5 gap-y-2">
                {selectedTaxonomies.map(t => (
                  <FormCheck
                    key={t.id}
                    label={t.name}
                    type="checkbox"
                    checked
                    onCheckedChange={() => handleRemoveTaxonomy(t)}
                  />
                ))}
              </div>
            )}

            {/* 2. Botão para Abrir o Modal de Seleção */}
            <div>
              <Button
                variant={buttonVariant}
                type="button"
                onClick={() => setIsOpenModalTaxonomies(true)}
              >
                {selectedTaxonomies?.length > 0 ? 'Editar matérias' : t('textsCommon.buttonAddMatters')}
              </Button>
            </div>
          </>
        )}
      </FormField>

      {/* 3. Modal de Seleção de Taxonomias */}
      {taxonomies?.length > 0 && isOpenModalTaxonomies && (
        <ModalTaxonomy
          taxonomies={taxonomies}
          selectedTaxonomies={selectedTaxonomies}
          setFieldValue={setFieldValue}
          open={taxonomies?.length > 0 && isOpenModalTaxonomies}
          onOpenChange={setIsOpenModalTaxonomies}
          closeModal={() => setIsOpenModalTaxonomies(false)}
        />
      )}
    </>
  );
}
